import verifyInviteTokenQuery from '@aurora/shared-client/components/authentication/RegistrationForm/VerifyInviteToken.query.graphql';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import TenantContext from '@aurora/shared-client/components/context/TenantContext';
import useAuthFlow from '@aurora/shared-client/components/useAuthFlow';
import useIsomorphicRedirect, {
  RedirectStatusCode
} from '@aurora/shared-client/components/useIsomorphicRedirect';
import useIsomorphicRedirectByUrl from '@aurora/shared-client/components/useIsomorphicRedirectByUrl';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import useRegistrationStatus from '@aurora/shared-client/components/users/useRegistrationStatus';
import usePageLoadingGlobalState from '@aurora/shared-client/helpers/ui/PageLoadingGlobalState';
import type { RouteWithQuery } from '@aurora/shared-client/routes/useCustomRouter';
import useEndUserNextPage from '@aurora/shared-client/routes/useEndUserNextPage';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import {
  EndUserComponent,
  EndUserPages,
  EndUserQueryParams
} from '@aurora/shared-types/pages/enums';
import { checkPolicy } from '@aurora/shared-utils/helpers/objects/PolicyResultHelper';
import { getLog } from '@aurora/shared-utils/log';
import React, { useContext } from 'react';
import ActionFeedbacks from '../../../components/common/ActionFeedback/ActionFeedbackTypes';
import BasePage from '../../../components/page/BasePage/BasePage';
import useTranslation from '../../../components/useTranslation';
import type {
  VerifyInviteTokenQuery,
  VerifyInviteTokenQueryVariables
} from '../../../types/graphql-types';

const log = getLog(module);
/**
 * Registration Page. Allows the user to a new account.
 *
 * @author Willi Hyde, Dolan Halbrook
 */
const RegistrationPage: React.FC<React.PropsWithChildren<unknown>> = () => {
  const tenant = useContext(TenantContext);
  const { router } = useEndUserRoutes();
  const { isAnonymous } = useRegistrationStatus();
  const { canAccess } = useContext(AppContext);
  const [pageLoading] = usePageLoadingGlobalState('pageLoading');
  const inviteToken = router.getUnwrappedQueryParam(EndUserQueryParams.INVITE_TOKEN);
  const { nextUrl, nextRoute } = useEndUserNextPage();
  const useInviteQuery = isAnonymous && inviteToken !== null;
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.REGISTRATION_PAGE
  );
  const { multiAuthEnabled } = tenant.publicConfig;
  const {
    getSsoAuthUrl,
    ssoPropertiesData,
    authProvidersData,
    loading: authFlowLoading
  } = useAuthFlow();
  const { community } = ssoPropertiesData || {};
  const ssoEnabled = checkPolicy(community.ssoProperties?.ssoEnabled);
  const finalUrl = getSsoAuthUrl(nextUrl, true);

  const {
    data,
    error: apiError,
    loading
  } = useQueryWithTracing<VerifyInviteTokenQuery, VerifyInviteTokenQueryVariables>(
    module,
    verifyInviteTokenQuery,
    {
      variables: {
        token: inviteToken
      },
      skip: !useInviteQuery
    }
  );

  const [localAuthProvider] = authProvidersData?.authProviders ?? [];
  const showRegistrationPage = multiAuthEnabled ? localAuthProvider?.enabled : !ssoEnabled;

  function shouldRedirect(): boolean {
    // Do not redirect if the invite query, data is loading, or sso is enabled
    if (loading || textLoading || pageLoading || authFlowLoading || ssoEnabled) {
      return false;
    }

    // Redirect if the user is not anonymous, or...
    // Redirect if the community does not allow anonymous and there is no invite token
    // which is required for anonymous users to register
    if (!isAnonymous || (!canAccess && inviteToken === null)) {
      return true;
    }

    // Redirect if there is an invalid response when verifying the invite token
    return !!(useInviteQuery && (apiError || data?.verifyInviteToken != null));
  }

  function createRedirectRoute(actionFeedback: ActionFeedbacks) {
    return {
      route: EndUserPages.LoginPage,
      params: {},
      query: {
        [EndUserQueryParams.ACTION_FEEDBACK]: actionFeedback,
        [EndUserQueryParams.TIMESTAMP]: Date.now().toString()
      }
    };
  }

  function getRedirectPage(): RouteWithQuery<EndUserPages, EndUserQueryParams> {
    // If the user is not anonymous then we redirect to the Community page or
    // redirect to next page route if next page query params are available
    if (!isAnonymous) {
      return nextRoute;
    }
    // If there was an error validating the invite token, then redirect to the Login
    // page with a toast message with an error message.
    if (useInviteQuery) {
      if (apiError) {
        log.error('GraphQL errors verifying invite token %s: %O', inviteToken, apiError);
        createRedirectRoute(ActionFeedbacks.INVITE_TOKEN_ERROR);
      }
      if (data?.verifyInviteToken != null) {
        log.error('Errors verifying invite token %s: %O', inviteToken, data.verifyInviteToken);
        createRedirectRoute(ActionFeedbacks.INVITE_TOKEN_INVALID);
      }
    }
    return { route: EndUserPages.LoginPage, params: {} };
  }
  // Redirect to SSO login page when SSO enabled and user is anonymous.
  // In case when Multiuth is enabled, Redirect to sign in page only if localAuthProvider is disabled.
  useIsomorphicRedirectByUrl(isAnonymous && !authFlowLoading && !showRegistrationPage, finalUrl);

  // Redirect to next page when SSO enabled and user is not anonymous
  useIsomorphicRedirect(!isAnonymous && !authFlowLoading && ssoEnabled, nextRoute);

  // Handle redirects for non-SSO flows.
  useIsomorphicRedirect(shouldRedirect(), getRedirectPage(), RedirectStatusCode.MOVED_TEMPORARILY);

  if (loading || textLoading || authFlowLoading) {
    return null;
  }

  return (
    <BasePage
      pageTitle={canAccess ? null : formatMessage('titleAnon')}
      pageId={EndUserPages.RegistrationPage}
    />
  );
};

export default RegistrationPage;
